import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowRight,
    RotateCcw,
    Loader2,
    Home,
    Plane,
    Target,
    Sparkles,
    Lock,
} from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const GOALS = [
    { key: "Buy my own home", label: "Buy my own home", icon: Home, hint: "250000" },
    { key: "Move abroad", label: "Move abroad", icon: Plane, hint: "30000" },
    { key: "Fund a big goal", label: "Fund a big goal", icon: Target, hint: "20000" },
    { key: "Build a real cushion", label: "Build a real cushion", icon: Sparkles, hint: "50000" },
];

const SYMBOL = { gbp: "£", usd: "$" };

const num = (s) => parseFloat(String(s).replace(/[^0-9.]/g, "")) || 0;

export const DreamMagnet = () => {
    const [step, setStep] = useState(0);
    const [currency, setCurrency] = useState("gbp");
    const [goal, setGoal] = useState("");
    const [cost, setCost] = useState("");
    const [monthly, setMonthly] = useState("");
    const [current, setCurrent] = useState("");
    const [form, setForm] = useState({ nome: "", email: "" });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null); // {months, years, monthsRem, year}

    const sym = SYMBOL[currency];
    const totalSteps = 5; // goal, cost, monthly, current, capture

    const next = () => setStep((s) => s + 1);

    const pickGoal = (g) => {
        setGoal(g);
        next();
    };

    const validAmountStep = () => {
        if (step === 1 && num(cost) <= 0) {
            toast.error("Add a rough cost for your goal.");
            return false;
        }
        if (step === 2 && num(monthly) <= 0) {
            toast.error("Add how much you can set aside each month.");
            return false;
        }
        return true;
    };

    const compute = () => {
        const remaining = Math.max(num(cost) - num(current), 0);
        const m = num(monthly);
        if (remaining === 0) return { months: 0, years: 0, monthsRem: 0, year: new Date().getFullYear() };
        const months = Math.ceil(remaining / m);
        const years = Math.floor(months / 12);
        const monthsRem = months % 12;
        const year = new Date().getFullYear() + Math.ceil(months / 12);
        return { months, years, monthsRem, year };
    };

    const submit = (e) => {
        e.preventDefault();
        if (!form.nome || !form.email) {
            toast.error("Add your name and email to see your timeline.");
            return;
        }
        setLoading(true);
        const r = compute();
        const safeMonths = Number.isFinite(r.months) ? r.months : 0;
        // Save the lead best-effort — never block the user's result on it.
        axios
            .post(`${API}/dream`, {
                nome: form.nome,
                email: form.email,
                goal,
                currency,
                cost: num(cost),
                monthly: num(monthly),
                current: num(current),
                months_estimate: safeMonths,
            })
            .catch((err) => {
                console.error("dream lead save failed", err);
            });
        // Always reveal the timeline (it's computed locally).
        setResult(r);
        setLoading(false);
        next();
    };

    const restart = () => {
        setStep(0);
        setGoal("");
        setCost("");
        setMonthly("");
        setCurrent("");
        setForm({ nome: "", email: "" });
        setResult(null);
    };

    const goContact = () => {
        if (!scrollToId("#pricing")) window.location.href = "/#pricing";
    };

    const progress = Math.round((Math.min(step, totalSteps) / totalSteps) * 100);

    return (
        <section
            id="plan"
            data-testid="plan-section"
            className="bg-[#070A13] text-[#FDFBF7] border-y border-white/10"
        >
            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-20 md:py-32">
                <div className="text-center mb-12">
                    <p className="overline text-[#C5A059] mb-6">
                        — Free 60-second preview
                    </p>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                        When will you reach your{" "}
                        <span className="italic text-[#C5A059]">dream</span>?
                    </h2>
                    <p className="font-body text-white/50 mt-5 max-w-lg mx-auto">
                        Run your real numbers and see the honest timeline — then
                        find out how much sooner it could be.
                    </p>
                </div>

                {/* progress */}
                {step < totalSteps && (
                    <div className="max-w-2xl mx-auto mb-12">
                        <div className="flex items-center justify-between mb-3">
                            <div className="h-1 flex-1 bg-white/10 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-[#C5A059]"
                                    animate={{ width: `${progress}%` }}
                                    transition={{ duration: 0.5, ease }}
                                />
                            </div>
                            {/* currency toggle */}
                            <div
                                data-testid="plan-currency"
                                className="ml-5 inline-flex items-center border border-white/15 rounded-full p-0.5 shrink-0"
                            >
                                {["gbp", "usd"].map((c) => (
                                    <button
                                        key={c}
                                        data-testid={`plan-currency-${c}`}
                                        onClick={() => setCurrency(c)}
                                        className={`overline px-3 py-1.5 rounded-full transition-colors ${
                                            currency === c
                                                ? "bg-[#11141D] text-[#FDFBF7]"
                                                : "text-white/50"
                                        }`}
                                    >
                                        {SYMBOL[c]}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="max-w-2xl mx-auto min-h-[300px]">
                    <AnimatePresence mode="wait">
                        {/* Step 0 — goal */}
                        {step === 0 && (
                            <motion.div
                                key="goal"
                                data-testid="plan-step-goal"
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -40 }}
                                transition={{ duration: 0.45, ease }}
                            >
                                <h3 className="font-display text-3xl md:text-4xl leading-tight mb-8">
                                    What do you really want?
                                </h3>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {GOALS.map((g) => {
                                        const Icon = g.icon;
                                        return (
                                            <button
                                                key={g.key}
                                                data-testid={`plan-goal-${g.key.replace(/\s/g, "-").toLowerCase()}`}
                                                onClick={() => pickGoal(g.key)}
                                                className="group flex items-center gap-4 text-left border border-white/15 hover:border-[#C5A059] hover:bg-white/[0.04] rounded-lg px-6 py-5 transition-colors duration-300"
                                            >
                                                <span className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center text-[#C5A059] group-hover:text-[#C5A059] transition-colors">
                                                    <Icon size={20} />
                                                </span>
                                                <span className="font-body text-lg text-white/90">
                                                    {g.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}

                        {/* Step 1 — cost */}
                        {step === 1 && (
                            <motion.div
                                key="cost"
                                data-testid="plan-step-cost"
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -40 }}
                                transition={{ duration: 0.45, ease }}
                            >
                                <h3 className="font-display text-3xl md:text-4xl leading-tight mb-3">
                                    Roughly, what will it cost?
                                </h3>
                                <p className="font-body text-white/50 mb-8">
                                    A ballpark is fine — the total amount you
                                    need for &ldquo;{goal}&rdquo;.
                                </p>
                                <AmountField
                                    sym={sym}
                                    value={cost}
                                    setValue={setCost}
                                    placeholder="250,000"
                                    testid="plan-input-cost"
                                />
                                <StepNext
                                    onClick={() => validAmountStep() && next()}
                                    testid="plan-next-cost"
                                />
                            </motion.div>
                        )}

                        {/* Step 2 — monthly */}
                        {step === 2 && (
                            <motion.div
                                key="monthly"
                                data-testid="plan-step-monthly"
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -40 }}
                                transition={{ duration: 0.45, ease }}
                            >
                                <h3 className="font-display text-3xl md:text-4xl leading-tight mb-3">
                                    How much can you set aside monthly?
                                </h3>
                                <p className="font-body text-white/50 mb-8">
                                    Be honest — what you realistically save each
                                    month right now.
                                </p>
                                <AmountField
                                    sym={sym}
                                    value={monthly}
                                    setValue={setMonthly}
                                    placeholder="400"
                                    testid="plan-input-monthly"
                                />
                                <StepNext
                                    onClick={() => validAmountStep() && next()}
                                    testid="plan-next-monthly"
                                />
                            </motion.div>
                        )}

                        {/* Step 3 — current */}
                        {step === 3 && (
                            <motion.div
                                key="current"
                                data-testid="plan-step-current"
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -40 }}
                                transition={{ duration: 0.45, ease }}
                            >
                                <h3 className="font-display text-3xl md:text-4xl leading-tight mb-3">
                                    How much have you saved so far?
                                </h3>
                                <p className="font-body text-white/50 mb-8">
                                    Put 0 if you&apos;re starting from scratch —
                                    no shame, that&apos;s the point.
                                </p>
                                <AmountField
                                    sym={sym}
                                    value={current}
                                    setValue={setCurrent}
                                    placeholder="0"
                                    testid="plan-input-current"
                                />
                                <StepNext
                                    onClick={next}
                                    testid="plan-next-current"
                                />
                            </motion.div>
                        )}

                        {/* Step 4 — capture */}
                        {step === 4 && (
                            <motion.form
                                key="capture"
                                data-testid="plan-capture"
                                onSubmit={submit}
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -40 }}
                                transition={{ duration: 0.45, ease }}
                            >
                                <h3 className="font-display text-3xl md:text-4xl leading-tight mb-4">
                                    Your timeline is ready.
                                </h3>
                                <p className="font-body text-white/60 mb-10 text-lg">
                                    Where should I send it — with the first move
                                    to bring your date closer?
                                </p>
                                <div className="flex flex-col gap-6">
                                    <input
                                        data-testid="plan-input-nome"
                                        className="editorial-input !text-[#FDFBF7] !border-white/20 placeholder:!text-white/30"
                                        placeholder="Your first name"
                                        value={form.nome}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                nome: e.target.value,
                                            }))
                                        }
                                    />
                                    <input
                                        data-testid="plan-input-email"
                                        type="email"
                                        className="editorial-input !text-[#FDFBF7] !border-white/20 placeholder:!text-white/30"
                                        placeholder="you@email.com"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                email: e.target.value,
                                            }))
                                        }
                                    />
                                    <button
                                        data-testid="plan-submit"
                                        type="submit"
                                        disabled={loading}
                                        className="group inline-flex items-center justify-center gap-3 bg-[#C5A059] text-[#070A13] font-body font-semibold text-lg rounded-full px-8 py-5 transition-transform duration-300 hover:-translate-y-1 disabled:opacity-60"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2
                                                    className="animate-spin"
                                                    size={20}
                                                />
                                                Running your numbers…
                                            </>
                                        ) : (
                                            <>
                                                Show my timeline
                                                <ArrowRight size={20} />
                                            </>
                                        )}
                                    </button>
                                    <p className="font-mono text-[11px] text-white/30 text-center">
                                        No spam. Just your timeline and one
                                        honest tip.
                                    </p>
                                </div>
                            </motion.form>
                        )}

                        {/* Step 5 — result */}
                        {step === 5 && result && (
                            <motion.div
                                key="result"
                                data-testid="plan-result"
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, ease }}
                                className="text-center"
                            >
                                <p className="overline text-white/40 mb-4">
                                    At your current pace
                                </p>
                                <p
                                    data-testid="plan-result-years"
                                    className="font-display text-6xl md:text-8xl tracking-tighter leading-none text-[#C5A059]"
                                >
                                    {result.months === 0
                                        ? "You're there"
                                        : result.years > 0
                                          ? `${result.years} yr${result.years > 1 ? "s" : ""}${result.monthsRem ? ` ${result.monthsRem}m` : ""}`
                                          : `${result.months} mo`}
                                </p>
                                {result.months > 0 && (
                                    <p className="font-body text-lg text-white/70 mt-6">
                                        You&apos;d reach &ldquo;{goal}&rdquo;
                                        around{" "}
                                        <span className="text-[#FDFBF7] font-semibold">
                                            {result.year}
                                        </span>
                                        .
                                    </p>
                                )}

                                {/* Withheld fast-lane */}
                                <div className="relative mt-10 border border-white/10 rounded-xl p-8 overflow-hidden">
                                    <div className="flex items-center justify-center gap-2 text-[#C5A059] mb-3">
                                        <Lock size={16} />
                                        <span className="overline">
                                            The fast lane
                                        </span>
                                    </div>
                                    <p className="font-body text-white/70 leading-relaxed max-w-md mx-auto select-none">
                                        There&apos;s a faster route hidden inside
                                        your own numbers. On a{" "}
                                        <span className="text-[#FDFBF7] font-semibold">
                                            Dream Plan call
                                        </span>{" "}
                                        I map the exact moves to bring this date
                                        forward — often by years. That part is
                                        too personal to guess here.
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
                                    <button
                                        data-testid="plan-result-cta"
                                        onClick={goContact}
                                        className="inline-flex items-center gap-2 bg-[#C5A059] text-[#070A13] font-body font-semibold rounded-full px-8 py-4 transition-transform duration-300 hover:-translate-y-1"
                                    >
                                        Bring my date closer — book the call
                                        <ArrowRight size={18} />
                                    </button>
                                    <button
                                        data-testid="plan-restart"
                                        onClick={restart}
                                        className="inline-flex items-center gap-2 text-white/50 hover:text-white font-body transition-colors duration-300"
                                    >
                                        <RotateCcw size={16} /> Start over
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};

const AmountField = ({ sym, value, setValue, placeholder, testid }) => (
    <div className="flex items-center gap-3 border-b border-white/20 focus-within:border-[#C5A059] transition-colors">
        <span className="font-display text-4xl md:text-5xl text-white/70">
            {sym}
        </span>
        <input
            data-testid={testid}
            inputMode="numeric"
            className="bg-transparent outline-none font-display text-4xl md:text-5xl text-[#FDFBF7] w-full py-3 placeholder:text-white/25"
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
        />
    </div>
);

const StepNext = ({ onClick, testid }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className="group mt-10 inline-flex items-center gap-3 bg-[#C5A059] text-[#070A13] font-body font-semibold rounded-full px-7 py-4 transition-transform duration-300 hover:-translate-y-1"
    >
        Continue
        <ArrowRight
            size={18}
            className="transition-transform duration-300 group-hover:translate-x-1"
        />
    </button>
);
